import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Printer, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * v6.46 — Printable shipping / audit manifest for a single USB order.
 *
 * Rendered at /admin/usbs/:order_id/manifest · super-admin only (gated
 * by the AppRouter ProtectedRoute). Intended to be printed to PDF or
 * on paper and handed to the buyer / procurement office / NYC DOE
 * audit trail. Deliberately plain HTML + inline styles so the browser
 * Print dialog produces a predictable one-page document on Letter.
 */
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 6, breakInside: 'avoid' }}>
    <div style={{
      fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6,
      color: '#6B7280', fontWeight: 700,
    }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0F172A', fontFamily: 'inherit' }}>
      {children || <span style={{ color: '#9CA3AF' }}>—</span>}
    </div>
  </div>
);

const Mono = ({ children }) => (
  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{children}</span>
);

const UsbManifest = () => {
  const { order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('mosaic_session_token');
        const { data } = await axios.get(`${API}/api/usb-orders/${order_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrder(data.order || null);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setErr(typeof detail === 'string' ? detail : (e.message || 'Could not load order'));
      } finally {
        setLoading(false);
      }
    })();
  }, [order_id]);

  if (loading) {
    return <div data-testid="manifest-loading" style={{ padding: 32, fontFamily: 'Helvetica, Arial, sans-serif' }}>Loading manifest…</div>;
  }
  if (err || !order) {
    return (
      <div data-testid="manifest-error" style={{ padding: 32, fontFamily: 'Helvetica, Arial, sans-serif', color: '#991B1B' }}>
        Could not load manifest: {err || 'order not found'}
      </div>
    );
  }

  const drives   = order.drives || [];
  const lineItem = (order.line_items || [])[0] || {};
  const buyer    = order.buyer_contact || {};
  const ship     = order.shipping_address || {};
  const repl     = order.physical_replication || null;
  const requested = (order.line_items || []).reduce((a, li) => a + (li.quantity || 1), 0);
  const minted    = drives.length;

  return (
    <div
      data-testid="usb-manifest"
      style={{
        maxWidth: 820, margin: '0 auto', padding: 36,
        fontFamily: 'Helvetica, Arial, sans-serif', color: '#0F172A',
        background: '#fff',
      }}
    >
      {/* Toolbar — hidden on print */}
      <div
        className="no-print"
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 20, paddingBottom: 10, borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Link
          to="/admin/usbs"
          data-testid="manifest-back"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: '#4B5563', textDecoration: 'none',
          }}
        >
          <ArrowLeft size={14} /> Back to USBs
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="manifest-print-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 18px', fontSize: 13, fontWeight: 700,
            background: '#0F1B2D', color: '#fff', border: 'none', cursor: 'pointer',
          }}
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          @page { size: Letter; margin: 0.6in; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 24, breakInside: 'avoid' }}>
        <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
          MOSAIC Language Proficiency Assessment · Sealed USB
        </div>
        <h1 style={{ fontSize: 26, margin: 0, color: '#0F1B2D' }}>Shipping &amp; Audit Manifest</h1>
        <div style={{ fontSize: 13, color: '#4B5563', marginTop: 6 }}>
          Order <Mono>{order.order_id}</Mono> · generated {new Date().toISOString().slice(0, 19).replace('T', ' ')} UTC
        </div>
      </div>

      {/* Buyer + Ship */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24,
        marginBottom: 20, breakInside: 'avoid',
      }}>
        <section>
          <h2 style={sectionH2}>Buyer</h2>
          <Field label="Name">{buyer.name}</Field>
          <Field label="Email">{buyer.email}</Field>
          <Field label="Phone">{buyer.phone}</Field>
          <Field label="School">{order.school_name}</Field>
          <Field label="District">{order.district_name}</Field>
        </section>
        <section>
          <h2 style={sectionH2}>Ship To</h2>
          <Field label="Street">{ship.line1}{ship.line2 ? ` · ${ship.line2}` : ''}</Field>
          <Field label="City">{ship.city}</Field>
          <Field label="State / ZIP">{ship.state} {ship.zip}</Field>
          <Field label="Country">{ship.country || 'USA'}</Field>
        </section>
      </div>

      {/* Order */}
      <section style={{ marginBottom: 20, breakInside: 'avoid' }}>
        <h2 style={sectionH2}>Order</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="PO Number">{order.po_number}</Field>
          <Field label="Quote Number">{order.quote_number}</Field>
          <Field label="Fulfillment Type">{order.fulfillment_type || '—'}</Field>
          <Field label="SKU"><Mono>{lineItem.sku}</Mono></Field>
          <Field label="Qty Ordered">{requested}</Field>
          <Field label="Unit Price">${(lineItem.unit_price || 0).toFixed(2)}</Field>
          <Field label="Grade Band">{order.initial_grade_band}</Field>
          <Field label="Status">{order.status}</Field>
          <Field label="Created">{(order.created_at || '').slice(0, 19).replace('T', ' ')} UTC</Field>
        </div>
      </section>

      {/* Drives */}
      <section style={{ marginBottom: 20, breakInside: 'avoid' }}>
        <h2 style={sectionH2}>Minted Drives ({minted} unique serial{minted === 1 ? '' : 's'})</h2>
        {drives.length === 0 ? (
          <div style={{ fontSize: 12, color: '#6B7280' }}>No drives minted yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F3F4F6', textAlign: 'left' }}>
                <th style={th}>Serial</th>
                <th style={th}>Band</th>
                <th style={th}>Status</th>
                <th style={th}>SHA-256 (first 16)</th>
                <th style={th}>Size (MB)</th>
              </tr>
            </thead>
            <tbody>
              {drives.map((d) => (
                <tr key={d.serial} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={td}><Mono>{d.serial}</Mono></td>
                  <td style={td}>{d.grade_band}</td>
                  <td style={td}>{d.status}</td>
                  <td style={td}><Mono>{(d.sha256 || '').slice(0, 16) || '—'}</Mono></td>
                  <td style={td}>{d.size_bytes ? (d.size_bytes / (1024 * 1024)).toFixed(1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Physical replication */}
      {repl && (
        <section style={{ marginBottom: 20, breakInside: 'avoid' }}>
          <h2 style={sectionH2}>Physical Replication</h2>
          <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
            This order ships <strong>{repl.stick_count} physical USB stick{repl.stick_count === 1 ? '' : 's'}</strong> from
            a single deterministic image (<Mono>{repl.source_serial}</Mono>). All sticks carry identical
            bytes, identical SHA-256, and identical password. They are distinguished only by the
            hand-labels below.
          </div>
          {repl.labels?.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F3F4F6', textAlign: 'left' }}>
                  <th style={th}>Stick #</th>
                  <th style={th}>Hand Label</th>
                  <th style={th}>Source Serial</th>
                </tr>
              </thead>
              <tbody>
                {repl.labels.map((lbl, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>{lbl}</td>
                    <td style={td}><Mono>{repl.source_serial}</Mono></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
            Recorded {repl.recorded_at ? repl.recorded_at.slice(0, 19).replace('T', ' ') + ' UTC' : '—'} by {repl.recorded_by || '—'}
          </div>
        </section>
      )}

      {/* Shipping */}
      <section style={{ marginBottom: 20, breakInside: 'avoid' }}>
        <h2 style={sectionH2}>Shipping</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Carrier">{order.carrier}</Field>
          <Field label="Tracking #"><Mono>{order.tracking_number}</Mono></Field>
          <Field label="Shipped At">{(order.shipped_at || '').slice(0, 19).replace('T', ' ')} UTC</Field>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ marginBottom: 20, breakInside: 'avoid' }}>
        <h2 style={sectionH2}>Audit Timeline ({(order.timeline || []).length})</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: '#F3F4F6', textAlign: 'left' }}>
              <th style={th}>At (UTC)</th>
              <th style={th}>Event</th>
              <th style={th}>From → To</th>
              <th style={th}>Actor</th>
            </tr>
          </thead>
          <tbody>
            {(order.timeline || []).map((t, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #E5E7EB' }}>
                <td style={td}>{(t.at || '').slice(0, 19).replace('T', ' ')}</td>
                <td style={td}>{t.event}</td>
                <td style={td}>{t.from || '—'} → {t.to || '—'}</td>
                <td style={td}>{t.user}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div style={{
        marginTop: 36, paddingTop: 10, borderTop: '1px solid #E5E7EB',
        fontSize: 10, color: '#6B7280', textAlign: 'center',
      }}>
        MOSAIC Assessment · Sealed USB · This manifest is an operational record and is not an invoice.
      </div>
    </div>
  );
};

const sectionH2 = {
  fontSize: 13, textTransform: 'uppercase', letterSpacing: 1,
  color: '#0F1B2D', margin: '0 0 10px 0', paddingBottom: 4,
  borderBottom: '2px solid #0F1B2D',
};
const th = { padding: '6px 8px', borderBottom: '1px solid #CBD5E1', fontWeight: 700 };
const td = { padding: '6px 8px', verticalAlign: 'top' };

export default UsbManifest;
